import type { AppLanguage } from "../api/types";
import type { DictationLocale } from "../platform/dictation";

/**
 * G-85 — the languages the kiosk speaks.
 *
 * English is the SOURCE language: `dictionaries/en.json` is the dictionary
 * every other one is checked against, and the key type is derived from it.
 * The four others are lazy-loaded chunks (see `store.ts`), so a florist who
 * never leaves English never downloads them.
 *
 * `label` is the language's own name for itself, never an English exonym —
 * the same rule G-27 set for the dictation select: a Ukrainian florist looks
 * for "Українська", not for "Ukrainian".
 *
 * `intl` is the BCP-47 tag handed to `Intl.PluralRules`, `toLocaleString`
 * and `<html lang>`. `dictation` is the Vosk model the note screen defaults
 * to when nothing has been chosen there (G-85 S4).
 */
export const KIOSK_LANGUAGES = [
  { code: "en", label: "English", intl: "en-GB", dictation: "en-GB" },
  { code: "es", label: "Español", intl: "es-ES", dictation: "es-ES" },
  { code: "ca", label: "Català", intl: "ca-ES", dictation: "ca-ES" },
  { code: "ru", label: "Русский", intl: "ru-RU", dictation: "ru-RU" },
  { code: "uk", label: "Українська", intl: "uk-UA", dictation: "uk-UA" },
] as const satisfies readonly {
  code: string;
  label: string;
  intl: string;
  dictation: DictationLocale;
}[];

export type KioskLanguage = (typeof KIOSK_LANGUAGES)[number]["code"];

/**
 * The STORE's resting language, and therefore the CRM's. English is the source
 * dictionary, so this is also what `translate` falls back to when a chunk has
 * not landed. Do not change it to make the kiosk Spanish — that is
 * `KIOSK_DEFAULT_LANGUAGE` below, and the two are deliberately different
 * facts: one is "the language the app is written in", the other is "the
 * language this shop's counter speaks".
 */
export const DEFAULT_LANGUAGE: KioskLanguage = "en";

/**
 * What the kiosk shows when it does not know who is standing at it — a fresh
 * tablet on its first run, or one whose site data was cleared (owner ruling
 * 2026-09-20: "lock screen default should be spanish, not english").
 *
 * It is only ever a FALLBACK. The tablet remembers the last language chosen
 * on it, and a florist's own language overrides both the moment their PIN is
 * accepted — which is the house rule this whole order settled on: a language
 * picker exists only where the app does not know who you are; everywhere else
 * the language follows the person.
 */
export const KIOSK_DEFAULT_LANGUAGE: KioskLanguage = "es";

/** localStorage key for the per-TABLET default — the ThemeProvider shape. */
export const LANG_STORAGE_KEY = "fw-lang";

/**
 * LIVE since 2026-09-21 (G-85 S5). The condition it waited for is met: both
 * ratchets read zero, so every kiosk string goes through the dictionary, and
 * taxonomy, money, dates, 24-hour clocks and the server's own refusals follow
 * the language too.
 *
 * ONE THING IS STILL OWED, and it is a layout risk rather than a wording one.
 * The five-language overflow pass covered the LOCK SCREEN only — clean at
 * 375 px and desktop — because every other kiosk screen is behind the PIN and
 * the agent does not enter one. The builder, waste flow, item card, note
 * panel, photo search and Shopify panel are verified by tests, not by eye, and
 * Russian and Ukrainian run about 1.5–2× the length of English on short
 * labels. The agent recommended merging with this still `false` and flipping
 * after that pass; the owner chose to flip now, and both sides are recorded
 * in FINALIZING §8 OD-65.
 *
 * Worth knowing when reading this flag: it gates the PICKER only. A person's
 * own language, from their `UserLanguagePreference` row, applies at PIN-in
 * whatever this says (S6) — so `false` never protected against the layout
 * risk, it only limited who could trigger it.
 */
export const SWITCHER_LIVE = true;

export function isKioskLanguage(value: unknown): value is KioskLanguage {
  return (
    typeof value === "string" &&
    KIOSK_LANGUAGES.some((language) => language.code === value)
  );
}

export function languageInfo(code: KioskLanguage) {
  const found = KIOSK_LANGUAGES.find((language) => language.code === code);
  // The union type guarantees a hit; the fallback only keeps TS honest.
  return found ?? KIOSK_LANGUAGES[0];
}

/** The BCP-47 tag for Intl APIs and `<html lang>`. */
export function intlLocale(code: KioskLanguage): string {
  return languageInfo(code).intl;
}

/**
 * G-85 S6 — the server spells a language in UPPERCASE (Prisma's `AppLanguage`
 * enum); this module spells it lowercase. The union itself is declared ONCE,
 * in `api/types.ts`, because it is a wire contract; here we only convert.
 *
 * `null` in, `null` out, and it stays null: "never chosen" is a real state,
 * distinct from "chose English", and collapsing the two would silently pin
 * every new florist to English.
 */
export function fromAppLanguage(value: string | null | undefined): KioskLanguage | null {
  if (!value) return null;
  const code = value.toLowerCase();
  return isKioskLanguage(code) ? code : null;
}

/**
 * Drift guard, and the reason there is no second copy of the union.
 *
 * `AppLanguage` is the wire contract (it mirrors the Prisma enum, and the
 * migration that created it); `KIOSK_LANGUAGES` is what the UI can actually
 * render. Adding a language means touching BOTH, plus a dictionary. If the
 * two ever disagree this type stops being `true` and the assignment fails
 * `tsc` — which is cheaper than discovering it as a blank dropdown option.
 */
type SameLanguages = [AppLanguage] extends [Uppercase<KioskLanguage>]
  ? [Uppercase<KioskLanguage>] extends [AppLanguage]
    ? true
    : false
  : false;
export const LANGUAGE_TABLES_AGREE: SameLanguages = true;

/**
 * The five languages as the CRM's selects want them — one list, derived from
 * `KIOSK_LANGUAGES`, so adding a language never means editing a second table.
 * Used by the Profile card and by Manage team.
 */
export const APP_LANGUAGE_OPTIONS: readonly {
  value: AppLanguage;
  label: string;
  lang: string;
}[] = KIOSK_LANGUAGES.map((language) => ({
  value: language.code.toUpperCase() as AppLanguage,
  label: language.label,
  lang: language.intl,
}));
